import {
	ResizableLayout,
	Skeleton,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	TabsTriggerWithNumber,
} from "@dune/ui";
import React from "react";
import { useCurrentPanel } from "../../stores/panel.store";
import PanelProvider from "./PanelProvider";

export interface Tab<T extends string = string> {
	value: T;
	label: string;
	notificationCount?: number;
}

interface PageLayoutWithTabsProps<T extends string = string> {
	title: string | React.ReactNode;
	tabs?: Tab<T>[];
	activeTab?: T;
	onTabChange?: (value: T) => void;
	headerActions?: React.ReactNode;
	topActions?: React.ReactNode;
	tabContent?: { [key: string]: React.ReactNode };
	children?: React.ReactNode;
}

const PageLayoutWithTabs = <T extends string = string>({
	title,
	headerActions,
	topActions,
	tabs,
	activeTab,
	onTabChange,
	tabContent,
	children,
}: PageLayoutWithTabsProps<T>) => {
	const currentPanel = useCurrentPanel();

	return (
		<ResizableLayout>
			<ResizableLayout.Main className="p-4 md:p-6">
				<div className="w-full h-full flex flex-col">
					{topActions && <div className="mb-2">{topActions}</div>}
					<div className="flex items-center justify-between mb-4">
						<div className="flex items-center gap-2">
							{typeof title === "string" ? (
								<h1 className="text-2xl font-semibold text-foreground">
									{title}
								</h1>
							) : (
								<div className="text-2xl font-semibold text-foreground">
									{title}
								</div>
							)}
						</div>
						{headerActions && <div>{headerActions}</div>}
					</div>
					{tabs && tabContent && (
						<div className="flex-1 flex flex-col min-h-0">
							<Tabs
								value={activeTab}
								onValueChange={(value) => onTabChange?.(value as T)}
								className="w-full flex-1 flex flex-col"
							>
								<TabsList>
									{tabs.map((tab) =>
										tab.notificationCount ? (
											<TabsTriggerWithNumber
												key={tab.value}
												value={tab.value}
												number={tab.notificationCount}
												variant="compact"
											>
												{tab.label}
											</TabsTriggerWithNumber>
										) : (
											<TabsTrigger
												key={tab.value}
												value={tab.value}
												variant="compact"
											>
												{tab.label}
											</TabsTrigger>
										),
									)}
								</TabsList>
								{tabs.map((tab) => (
									<TabsContent
										key={tab.value}
										value={tab.value}
										className="flex-1 overflow-y-auto mt-4"
									>
										{tabContent[tab.value]}
									</TabsContent>
								))}
							</Tabs>
						</div>
					)}
					{children && (
						<div className="flex-1 flex flex-col min-h-0">{children}</div>
					)}
				</div>
			</ResizableLayout.Main>
			{currentPanel && (
				<ResizableLayout.Panel>
					<PanelProvider />
				</ResizableLayout.Panel>
			)}
		</ResizableLayout>
	);
};

export const TabLayoutSkeleton: React.FC<{ tabCount?: number }> = ({
	tabCount = 5,
}) => {
	return (
		<ResizableLayout>
			<ResizableLayout.Main className="p-4 md:p-6">
				<div className="w-full h-full flex flex-col">
					<Skeleton className="mb-2 h-8 w-40" />
					<div className="flex items-center justify-between mb-4">
						<Skeleton className="h-8 w-64 rounded-md" />
						<Skeleton className="h-8 w-24 rounded-md" />
					</div>
					<div className="w-full">
						<div className="flex gap-2 mb-4">
							{Array.from({ length: tabCount }).map((_, i) => (
								<Skeleton
									// biome-ignore lint/suspicious/noArrayIndexKey: This is a skeleton
									key={`tab-skeleton-${i}`}
									className="h-8 w-24 rounded-md"
								/>
							))}
						</div>
						<Skeleton className="h-64 w-full rounded-md" />
					</div>
				</div>
			</ResizableLayout.Main>
		</ResizableLayout>
	);
};

export const TabLayoutErrorSkeleton: React.FC<{
	message?: string;
	tabCount?: number;
}> = ({ message = "Une erreur est survenue", tabCount = 5 }) => {
	return (
		<ResizableLayout>
			<ResizableLayout.Main className="p-4 md:p-6">
				<div className="w-full h-full min-h-[400px] flex flex-col relative">
					<div className="absolute inset-0 filter blur-[8px] opacity-70 pointer-events-none">
						<Skeleton className="mb-2 h-8 w-40" />
						<div className="flex items-center justify-between mb-4">
							<Skeleton className="h-8 w-64 rounded-md" />
							<Skeleton className="h-8 w-24 rounded-md" />
						</div>
						<div className="w-full">
							<div className="flex gap-2 mb-4">
								{Array.from({ length: tabCount }).map((_, i) => (
									<Skeleton
										// biome-ignore lint/suspicious/noArrayIndexKey: This is a skeleton
										key={`tab-skeleton-${i}`}
										className="h-8 w-24 rounded-md"
									/>
								))}
							</div>
							<Skeleton className="h-64 w-full rounded-md" />
						</div>
					</div>

					<div className="absolute inset-0 flex items-center justify-center">
						<div className="bg-destructive/10 text-destructive font-medium p-4 rounded-lg shadow-md border border-destructive/20 backdrop-blur-sm">
							{message}
						</div>
					</div>
				</div>
			</ResizableLayout.Main>
		</ResizableLayout>
	);
};

export default PageLayoutWithTabs;
